import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, Copy, ExternalLink, Check } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

const WalletRow = ({ label, address, icon, color }) => {
  const [copied, setCopied] = useState(false);

  const copyAddress = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success(`${label} address copied!`);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ backgroundColor: `${color}15` }}>
          <span className="text-lg">{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium mb-1">{label}</p>
          <code className="text-xs text-muted-foreground font-mono block truncate">
            {address}
          </code>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={copyAddress}
          data-testid={`copy-${label.toLowerCase().replace(' ', '-')}-address`}
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.open(`https://etherscan.io/address/${address}`, '_blank')}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const WalletAddressesCard = ({ wallets, stakingData }) => {
  if (!wallets) return null;

  const stakedAmount = stakingData?.staked_amount ? 
    (parseInt(stakingData.staked_amount) / Math.pow(10, 18)).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }) : '0';

  return (
    <Card data-testid="wallet-addresses-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            <CardTitle>Wallet Addresses</CardTitle>
          </div>
          <Badge variant="secondary" className="tabular-nums">
            {stakedAmount} HERMES Staked
          </Badge>
        </div>
        <CardDescription>
          Your hotel's blockchain wallets for token management
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <WalletRow 
          label="Treasury Wallet"
          address={wallets.treasury}
          icon="🏦"
          color="#3b82f6"
        />
        <WalletRow 
          label="Bitsy Agent Wallet"
          address={wallets.bitsy_agent}
          icon="🤖"
          color="#8b5cf6"
        />
        <WalletRow 
          label="Marina Agent Wallet"
          address={wallets.marina_agent}
          icon="🌊"
          color="#06b6d4"
        />
        
        <div className="pt-3 border-t">
          <p className="text-xs text-muted-foreground mb-2">Staking Overview</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 bg-muted/50 rounded">
              <p className="text-xs text-muted-foreground">Current APY</p>
              <p className="text-lg font-bold tabular-nums">
                {((stakingData?.current_apy || 0.15) * 100).toFixed(1)}%
              </p>
            </div>
            <div className="p-2 bg-muted/50 rounded">
              <p className="text-xs text-muted-foreground">Total Rewards</p>
              <p className="text-lg font-bold tabular-nums">
                {stakingData?.total_rewards ? 
                  (parseInt(stakingData.total_rewards) / Math.pow(10, 18)).toFixed(0) : '0'}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WalletAddressesCard;
